import { inject, Injectable, OnDestroy } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { APP_CONSTANTS } from 'src/app/app.constants';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

export interface WsMessage {
  header: { name: string; [key: string]: unknown };
  body: { [key: string]: any } | null;
}

@Injectable({ providedIn: 'root' })
export class WsService implements OnDestroy {
  private socket: WebSocket | null = null;
  private readonly messages$ = new Subject<WsMessage>();
  private readonly connected$ = new BehaviorSubject<boolean>(false);
  private _internalSubscriptions: Subscription[] = [];
  // Stores the last successful (status 4000) message per event name so that new subscribers
  // receive the most recent known state immediately, without waiting for the next server push.
  private readonly _lastMessages = new Map<string, WsMessage>();

  // Stored so reconnect attempts can reuse the same URL without callers needing to pass it again.
  private _wsUrl = '';
  // When true, a close event was triggered intentionally (logout, destroy) and should NOT
  // trigger the auto-reconnect loop.
  private _manualClose = false;
  // Tracks how many consecutive reconnect attempts have been made since the last clean connect.
  private _reconnectAttempts = 0;
  // Give up after this many failed attempts and show a persistent error to the user.
  // HMI retries forever: the panel has no operator to tap a retry toast.
  private readonly _maxReconnectAttempts = environment.isHMIMode ? Infinity : 5;
  // Starting delay (ms) for the exponential back-off: 5s → 10s → 20s … capped at _maxReconnectDelay.
  private readonly _baseReconnectDelay = 5000;
  // Upper bound on back-off delay: 30 seconds on the HMI (local subnet), 5 minutes on the web.
  private readonly _maxReconnectDelay = environment.isHMIMode ? 30000 : 300000;
  // Reference to the pending setTimeout so it can be canceled if the service is destroyed
  // before the timer fires (avoids trying to connect after logout/destroy).
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // True once a message has been received on the current connection. Used to distinguish
  // a "socket opened but server rejected and closed immediately" cycle (version mismatch,
  // auth failure, etc.) from a genuinely healthy connection — _reconnectAttempts is only
  // reset when data actually flows, preventing infinite reconnect loops on bad configs.
  private _receivedMessageOnCurrentConnection = false;
  // HMI only: how long a reopened socket must stay open before the page reloads. The controller
  // sends nothing unasked, so waiting for a first message would leave every tab but Well Status
  // frozen; a socket the controller rejects closes well inside this window, so it cannot loop.
  private readonly _hmiReloadDelay = 3000;
  // Pending HMI reload for the current socket, canceled if the socket closes before it fires.
  private _hmiReloadTimer: ReturnType<typeof setTimeout> | null = null;

  private toastr = inject(ToastrService);
  private auth = inject(AuthService);

  constructor() {
    this.connect(environment.wsURL);
  }

  /** Opens a WebSocket connection to the given URL, appending the app version and
   *  access token as query parameters. In HMI mode the token is omitted (no auth required).
   *  Wires up open/close/error/message handlers and broadcasts connection state changes. */
  connect(url: string): void {
    const token = this.auth.getAccessToken();
    if (!token && !environment.isHMIMode) {
      // No session yet (first load, expired token, or mid-redirect to Okta login).
      // This is expected pre-login state, not a connection failure — skip silently.
      return;
    }

    // Persist the URL so _scheduleReconnect can reuse it without extra parameters.
    this._wsUrl = url;
    // Reset the flag so that unexpected closes trigger auto-reconnect.
    this._manualClose = false;
    const connection = `${url}/?appVersion=${environment.connectionVersion}&accessToken=${token}`;

    this.socket = new WebSocket(connection);

    this.socket.onopen = () => {
      this._receivedMessageOnCurrentConnection = false;
      if (environment.isHMIMode && this._reconnectAttempts > 0) {
        this._hmiReloadTimer = setTimeout(() => window.location.reload(), this._hmiReloadDelay);
      }
      this.connected$.next(true);
    };

    this.socket.onclose = () => {
      this._clearHmiReloadTimer();
      this.connected$.next(false);
      // Only attempt to reconnect if the close was unexpected (network drop, server restart, etc.).
      // A manual close (logout, service destroy) sets _manualClose = true beforehand to skip this.
      if (!this._manualClose) {
        this._scheduleReconnect();
      }
    };

    // The browser always fires onclose right after onerror, so all recovery logic lives in
    // onclose to keep it in one place. This handler intentionally does nothing.
    this.socket.onerror = () => {
      /* intentionally empty — onclose handles reconnect */
    };

    this.socket.onmessage = (e) => {
      if (!this._receivedMessageOnCurrentConnection) {
        this._receivedMessageOnCurrentConnection = true;
        // Only reset the back-off counter and show the "reconnected" toast once real data
        // arrives — not on onopen. A socket can open and immediately be closed by the server
        // (version mismatch, auth rejection) without ever sending a message, so counting that
        // as a successful connection would reset the attempt counter and loop forever.
        if (this._reconnectAttempts > 0) {
          if (environment.isHMIMode) {
            // HMI mode: reload the page so all server-side subscriptions are re-established
            // from a clean ngOnInit, without waiting out the open-socket timer set in onopen.
            this._clearHmiReloadTimer();
            window.location.reload();
            return;
          }
          this.toastr.success('WebSocket reconnected.', 'Connection Restored');
        }
        this._reconnectAttempts = 0;
      }
      let msg: WsMessage;
      try {
        // The server occasionally emits bare Infinity/-Infinity/NaN tokens for numeric fields
        // (e.g. a loading value computed via divide-by-zero), which are invalid per the JSON spec
        // and make JSON.parse throw. Coerce them to null so one bad field doesn't drop the whole message.
        msg = JSON.parse(e.data.replace(/:\s*(-?Infinity|NaN)\s*(?=[,}])/g, ': null'));
      } catch (err) {
        console.warn('WsService: received malformed JSON message, discarding.', err, '');
        return;
      }
      if (msg.header['status'] === 4000) {
        this._lastMessages.set(msg.header['name'] as string, msg);
      }
      this.messages$.next(msg);
    };
  }

  /** Waits for an exponentially increasing delay then calls connect() again.
   *  Each failed attempt doubles the wait time, up to _maxReconnectDelay.
   *  Gives up and shows a persistent error after _maxReconnectAttempts failures; never in HMI
   *  mode, where the limit is Infinity. */
  private _scheduleReconnect(): void {
    if (this._reconnectAttempts >= this._maxReconnectAttempts) {
      const lostToast = this.toastr.error(
        'Tap to manually retry.',
        'Could not reconnect to the server after multiple attempts.',
        { disableTimeOut: true, tapToDismiss: true },
      );
      this._internalSubscriptions.push(
        lostToast.onTap.pipe(take(1)).subscribe(() => window.location.reload()),
      );
      return;
    }

    // Exponential back-off: 5s, 10s, 20s, 40s … capped at _maxReconnectDelay.
    // Using the current attempt count (before incrementing) as the exponent means
    // the first retry happens after _baseReconnectDelay * 2^0 = 5 s.
    const delay = Math.min(
      this._baseReconnectDelay * 2 ** this._reconnectAttempts,
      this._maxReconnectDelay,
    );
    this._reconnectAttempts++;

    const attemptLabel = Number.isFinite(this._maxReconnectAttempts)
      ? `${this._reconnectAttempts}/${this._maxReconnectAttempts}`
      : `${this._reconnectAttempts}`;
    this.toastr.warning(`Reconnecting… (attempt ${attemptLabel})`, 'Connection Lost', {
      progressBar: true,
      timeOut: delay,
    });

    // Schedule the reconnect and store the timer handle so ngOnDestroy can cancel it
    // if the service is torn down while a retry is pending.
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this.connect(this._wsUrl);
    }, delay);
  }

  /** Sends a message immediately if the socket is open. Shows an error toast if not connected. */
  sendRequest(message: WsMessage): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else if (!this._manualClose) {
      this.toastr.error('WebSocket is not connected.', 'Connection Error', {
        disableTimeOut: true,
        tapToDismiss: true,
      });
    }
  }

  /** Returns an Observable that filters the shared message stream to a specific event name.
   *  Replays the last known successful response immediately (if one is cached), then continues
   *  with live messages. Error toasts are only shown for live messages, not cached replays. */
  returnResponse(eventName: string): Observable<WsMessage> {
    const live$ = this.messages$.pipe(filter((msg) => msg.header['name'] === eventName));
    // Error toasts fire on live messages only — replaying a stale error would be misleading.
    const sub = live$.subscribe((message) => {
      if (message.header['status'] !== 4000) {
        const summary = message.header?.['summary'];
        if (summary === APP_CONSTANTS.RESPONSE_SUMMARIES.UPDATE_SUCCESS_BUT_CONTROLLER_FAILED) {
          this.toastr.info(
            'Well info saved successfully... But failed to propagate to the controller.',
            '',
            {
              progressBar: true,
              toastClass: 'ngx-toastr toast-info toast-white-info w-100 w-md-50 w-lg-25',
            },
          );
        } else {
          this.toastr.error(eventName + '->' + message.header['summary'], 'Error', {
            disableTimeOut: true,
            tapToDismiss: true,
          });
        }
      }
    });
    this._internalSubscriptions.push(sub);
    const cached = this._lastMessages.get(eventName);
    return cached ? merge(of(cached), live$) : live$;
  }

  /** Evicts all cached last-messages. Call when switching to a different well so that
   *  new tab components do not receive stale state from the previous well. */
  clearMessageCache(): void {
    this._lastMessages.clear();
  }

  /** Subscribes to a WebSocket event by name, calling the provided callback for each message. */
  subscribeWs(event: string, cb: (msg: WsMessage) => void): void {
    const sub = this.returnResponse(event).subscribe(cb);
    this._internalSubscriptions = this._internalSubscriptions || [];
    this._internalSubscriptions.push(sub);
  }

  /** Subscribes to raw messages for an event without the toast side-effect.
   *  Returns a Subscription the caller must unsubscribe on destroy. */
  subscribeWsManaged(event: string, cb: (msg: WsMessage) => void): Subscription {
    return this.messages$.pipe(filter((msg) => msg.header['name'] === event)).subscribe(cb);
  }

  /** Like subscribeWsManaged but also replays the last cached message immediately,
   *  so subscribers receive the current known state without waiting for the next push. */
  subscribeWsManagedWithReplay(event: string, cb: (msg: WsMessage) => void): Subscription {
    const live$ = this.messages$.pipe(filter((msg) => msg.header['name'] === event));
    const cached = this._lastMessages.get(event);
    const stream$ = cached ? merge(of(cached), live$) : live$;
    return stream$.subscribe(cb);
  }

  /** Queues a request to be sent as soon as the WebSocket is connected.
   *  Uses take(1) so the inner subscription self-cleans after the first open event. */
  sendWsRequest(header: WsMessage['header'], body: WsMessage['body']): void {
    this.onConnectionChange()
      .pipe(filter(Boolean), take(1))
      .subscribe(() => this.sendRequest({ header, body }));
  }

  /** Returns an Observable that emits true when the socket opens and false when it closes. */
  onConnectionChange(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  /** Closes the WebSocket without destroying the service.
   *  Sets _manualClose so the onclose handler skips auto-reconnect.
   *  Used by InactivityService to drop the connection after a timeout. */
  disconnect(): void {
    this._manualClose = true;
    if (this._reconnectTimer !== null) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._clearHmiReloadTimer();
    this.socket?.close();
  }

  /** Closes the WebSocket and completes all internal subjects to prevent memory leaks. */
  ngOnDestroy(): void {
    // Signal that this close is intentional so the onclose handler skips auto-reconnect.
    this._manualClose = true;
    // Cancel any pending reconnect timer — no point connecting after the service is destroyed.
    if (this._reconnectTimer !== null) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._clearHmiReloadTimer();
    this.socket?.close();
    this.messages$.complete();
    this.connected$.complete();
    for (const sub of this._internalSubscriptions) {
      sub.unsubscribe();
    }
    this._internalSubscriptions = [];
  }

  /** Cancels a pending HMI reload, if any. */
  private _clearHmiReloadTimer(): void {
    if (this._hmiReloadTimer !== null) {
      clearTimeout(this._hmiReloadTimer);
      this._hmiReloadTimer = null;
    }
  }
}
